import { useEffect, useState } from "react";
import { generateAdjustDeepLink } from "./adjustHelper";
import { GotradeConstant } from "./constant";
import {
  appendClickIdsToLink,
  buildPaidAdjustArgs,
  getCampaignAttribution,
  isGtiPathname,
  sanitizePathForAdjust,
} from "./campaign-attribution";
import { downloadCtaPlacement } from "./adjust-page-tag";

class ScrollDirection {
  static UP = "UP";
  static DOWN = "DOWN";
}

function useScrollDirection(initialScroll = ScrollDirection.DOWN) {
  const [scrollDir, setScrollDir] = useState(initialScroll);

  useEffect(() => {
    const threshold = 0;
    let lastScrollY = window.pageYOffset;
    let ticking = false;

    const updateScrollDir = () => {
      const scrollY = window.pageYOffset;

      if (Math.abs(scrollY - lastScrollY) < threshold) {
        ticking = false;
        return;
      }
      setScrollDir(
        scrollY > lastScrollY ? ScrollDirection.DOWN : ScrollDirection.UP
      );
      lastScrollY = scrollY > 0 ? scrollY : 0;
      ticking = false;
    };

    const onScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(updateScrollDir);
        ticking = true;
      }
    };

    window.addEventListener("scroll", onScroll);

    return () => window.removeEventListener("scroll", onScroll);
  }, [scrollDir]);

  return scrollDir;
}

function useYScrollPercentage() {
  const [yScrollPercentage, setYScrollPercentage] = useState(0);

  useEffect(() => {
    const updateScrollPercentage = () => {
      const docEl = document.documentElement;
      const docBody = document.body;

      const scrollPercentage =
        Math.round(
          ((docEl.scrollTop ?? docBody.scrollTop) /
            ((docEl.scrollHeight ?? docBody.scrollHeight) -
              docEl.clientHeight)) *
            10000
        ) / 100;

      setYScrollPercentage(scrollPercentage ?? yScrollPercentage ?? 0);
    };

    window.addEventListener("scroll", updateScrollPercentage);

    return () => window.removeEventListener("scroll", updateScrollPercentage);
  }, [yScrollPercentage]);

  return yScrollPercentage;
}

const getCookie = (name) => {
  var match =
    (typeof document !== "undefined" &&
      document.cookie.match(new RegExp("(^| )" + name + "=([^;]+)"))) ||
    null;
  if (match) return match[2];

  return null;
};

// Fallback URLs when adjust link generation fails
const FALLBACK_LINKS = {
  gtg: GotradeConstant.GTG_APP_DOWNLOAD_ADJUST_DEFAULT_LINK,
  gti: GotradeConstant.GTI_APP_DOWNLOAD_ADJUST_DEFAULT_LINK,
};

// SEOENG-117: the Adjust adgroup is the page the visitor is on. `path` only decides whether a CTA
// asks for a generated link at all; several callers pass a section label rather than the page
// (every ticker page passed the hub /id/saham-as/), which credited the signup to the hub.
function currentPageAdgroup(fallbackPath) {
  const pathname = typeof window !== "undefined" ? window.location.pathname : "";
  return sanitizePathForAdjust(pathname || fallbackPath || "");
}

function useOrganicAdjustLink(path) {
  const [adjustLink, setAdjustLink] = useState();

  useEffect(() => {
    if (!path) return;

    async function fetchAdjustLink() {
      try {
        const gaClientId = getCookie("_ga");
        const label = `client_id=${gaClientId}`;

        const link = await generateAdjustDeepLink(
          'Download_banner_btn',
          true,
          '',
          label,
          'Gotrade | Download App',
          "Invest in US stocks, starting from $1.",
          'gtg',
          'organic',
          currentPageAdgroup(path),
        );

        if (link) {
          setAdjustLink(link);
        } else {
          // API returned empty, use fallback
          setAdjustLink(FALLBACK_LINKS.gtg);
        }
      } catch (error) {
        console.error('Failed to generate adjust link:', error);
        setAdjustLink(FALLBACK_LINKS.gtg);
      }
    }

    fetchAdjustLink();
  }, [path]);

  return adjustLink;
}

const GTI_DOWNLOAD_OG_TITLE = 'Gotrade Indonesia | Download Aplikasi';
const GTI_DOWNLOAD_OG_DESCRIPTION =
  "Mulai Investasi di Gotrade, aplikasi investasi saham luar negeri. Mulai dari Rp 15 Ribu!";

// The ID download link every GTI acquisition CTA uses. Organic by default; on a visit that carries
// ad-source parameters (utm_*, gclid/gbraid/wbraid, kept for the session) it becomes a PAID link
// attributed to that channel and campaign, with the Google click ids appended, so the install and
// the funded-account event that follows are attributed to the ad instead of "organic" (SEOENG-38).
// Organic visits with no path keep the pre-existing behaviour (no API call, callers fall back to
// GTI_APP_DOWNLOAD_ADJUST_DEFAULT_LINK); a paid visit on a GTI route generates regardless, using
// the page path.
//
// placement (header | hero | sticky | footer, see ADJUST_PLACEMENTS) rides the organic link as the
// Adjust creative, so a signup can be split by the button that sent it (DAR-235 follow-up); a
// caller that names none is an in-body CTA and sends body (SEOENG-167). A paid link keeps its own
// creative (the ad's utm_term).
function useIdOrganicAdjustLink(path, placement = '') {
  const [adjustLink, setAdjustLink] = useState();
  const creative = downloadCtaPlacement(placement);

  useEffect(() => {
    const attribution = getCampaignAttribution();
    // A paid visit generates even when the caller passed no path (those CTAs previously had NO
    // Adjust link at all and fell back to the static store links -- the gap this fixes). Scoped to
    // GTI routes: this hook is also mounted on /en/ pages where its result is never rendered.
    const pathname = typeof window !== 'undefined' ? window.location.pathname : '';
    const paidFallbackPath = attribution && isGtiPathname(pathname) ? pathname : '';
    const effectivePath = path || paidFallbackPath;
    if (!effectivePath) return;

    async function fetchAdjustLink() {
      try {
        const gaClientId = getCookie("_ga");
        const paid = attribution
          ? buildPaidAdjustArgs(attribution, { gaClientId, path: effectivePath })
          : null;

        const link = paid
          ? await generateAdjustDeepLink(
              paid.campaign,
              false,
              '',
              paid.label,
              GTI_DOWNLOAD_OG_TITLE,
              GTI_DOWNLOAD_OG_DESCRIPTION,
              'gti',
              // The backend accepts only app|organic here and uses it to pick the LINK TOKEN;
              // "organic" is the website link. The paid channel itself is carried in the campaign
              // name prefix and the label, not in this field.
              'organic',
              paid.adgroup,
              paid.creative,
            )
          : await generateAdjustDeepLink(
              'Download_banner_btn',
              true,
              '',
              `client_id=${gaClientId}`,
              GTI_DOWNLOAD_OG_TITLE,
              GTI_DOWNLOAD_OG_DESCRIPTION,
              'gti',
              'organic',
              currentPageAdgroup(effectivePath),
              creative,
            );

        if (link) {
          setAdjustLink(paid ? appendClickIdsToLink(link, attribution) : link);
        } else {
          // API returned empty, use fallback (tagged with page + placement at the exit)
          setAdjustLink(FALLBACK_LINKS.gti);
        }
      } catch (error) {
        console.error('Failed to generate adjust link:', error);
        setAdjustLink(FALLBACK_LINKS.gti);
      }
    }

    fetchAdjustLink();
  }, [path, creative]);

  return adjustLink;
}

function isBrowserSafari() {
  return /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
}

/**
 * Determine the mobile operating system.
 * This function returns one of 'iOS', 'Android', 'Windows Phone', or 'unknown'.
 *
 * @returns {String}
 */
function getMobileOperatingSystem() {
  if (typeof window === undefined) {
    return "unknown";
  }

  const userAgent = window.navigator.userAgent;
  const platform = window.navigator.platform;
  const macosPlatforms = ["Macintosh", "MacIntel", "MacPPC", "Mac68K"];
  const windowsPlatforms = ["Win32", "Win64", "Windows", "WinCE"];
  const iosPlatforms = ["iPhone", "iPad", "iPod"];

  if (macosPlatforms.indexOf(platform) !== -1) {
    return "Mac OS";
  }

  if (iosPlatforms.indexOf(platform) !== -1) {
    return "iOS";
  }

  if (windowsPlatforms.indexOf(platform) !== -1) {
    return "Windows";
  }

  if (/Android/.test(userAgent)) {
    return "Android";
  }

  if (/Linux/.test(platform)) {
    return "Linux";
  }

  return "unknown";
}

export {
  ScrollDirection,
  useScrollDirection,
  useYScrollPercentage,
  useOrganicAdjustLink,
  useIdOrganicAdjustLink,
  getMobileOperatingSystem,
  isBrowserSafari,
};
